import { useEffect, useMemo, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import { analyticsSessionId } from 'lib/analyticsSession'
import {
    EngagementSurface,
    UNKNOWN_SURFACE,
    resolveEngagementSurface,
    sameEngagementSurfaceContext,
} from 'lib/engagementSurface'
import {
    QUEST_IMPRESSION_VISIBLE_RATIO,
    QueuedEngagementEvent,
    QueuedQuestImpression,
    engagementEventKey,
    qualifiesQuestImpression,
    questImpressionKey,
    questImpressionThresholdForHeight,
    serializeEngagementEvents,
    serializeQuestImpressions,
} from 'lib/questImpressionPolicy'
import useStore from 'state/knovStore'

// Stream tracking, two outputs from one observer.
//
// 1. IMPRESSIONS ("seen") for TOP NEW: records that a quest's card was actually
//    SHOWN to the user in a stream — visible past a dwell threshold — as opposed
//    to "visited" (opened the thread, quest_visits). The server merges both into
//    the TOP NEW unseen tier, so scrolling past a card is enough to drain it;
//    you never have to click through. This is a forward-only projection, one row
//    per (user, space, quest), and it is load-bearing.
//
// 2. ENGAGEMENT EVENTS: an append-only log of the same exposures carrying what
//    the projection deliberately throws away — measured dwell, the surface and
//    ordering the card appeared in, its rank position, and whether the exposure
//    turned into an open. Nothing reads these today; they exist because a future
//    personalized ranking cannot be trained on data nobody was collecting. See
//    docs/engagement-metrics-for-you.md.
//
// Both ride the same batched flush (every few seconds, and on pagehide with
// keepalive). The event half is strictly additive: if it breaks, TOP NEW does
// not.

// A card counts as "shown" when it stays qualifying for the full dwell — brief
// flashes while fling-scrolling do not burn threads.
const DWELL_MS = 1000
// Qualifying visibility: >= 40% of the card, OR >= 240px of it (tall cards can
// never reach a high intersection ratio, so the pixel floor covers them).
const FLUSH_DELAY_MS = 4000
const MAX_BATCH = 50
// A parked tab would otherwise accumulate unbounded "visible" time and dominate
// any dwell distribution computed from this log.
const MAX_TRACKED_DWELL_MS = 5 * 60 * 1000
// Bounds the open-attribution map; far more than a viewport's worth of rows.
const MAX_REMEMBERED_EXPOSURES = 300
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const queuedImpressions = new Map<string, QueuedQuestImpression>()
const recordedImpressions = new Set<string>()
const queuedEvents = new Map<string, QueuedEngagementEvent>()
const sentEventKeys = new Set<string>()
// Finalizers for exposures still on screen, so a page teardown can close them
// before the last flush instead of losing their dwell.
const activeExposures = new Set<() => void>()
// Where each subject was last shown, so an open can be attributed to the rank
// and ordering the user actually clicked from — the target panel's own filter
// cannot know that.
const recentExposures = new Map<string, ExposureContext>()
let flushTimer: number | null = null
let flushListenersRegistered = false
// Flipped on auth/capability rejection so a misconfigured client goes quiet instead
// of retrying forever.
let trackingDisabled = false

interface ExposureContext extends EngagementSurface {
    spaceId: string
    position?: number | null
}

export function questImpressionsEnabled(): boolean {
    if (trackingDisabled) return false
    const user = window.gon?.currentUser
    if (!user) return false
    return user.can_top_new ?? false
}

function activeSpaceId(): string | null {
    return (
        useStore.getState().activeSpaceId ||
        window.gon?.currentUser?.space_id ||
        window.gon?.KNOVIGATOR_SPACE_ID ||
        null
    )
}

function activeUserId(): string | null {
    return window.gon?.currentUser?.id || null
}

function validUuid(value?: string | null): string | null {
    return value && UUID_RE.test(value) ? value : null
}

function scheduleFlush() {
    if (flushTimer != null) return
    flushTimer = window.setTimeout(() => {
        flushTimer = null
        void flushQuestImpressions()
    }, FLUSH_DELAY_MS)
}

// Closing open exposures first is what makes the final flush carry real dwell
// for whatever is on screen at teardown, rather than dropping it.
function finalizeActiveExposures() {
    Array.from(activeExposures).forEach(finalize => {
        try {
            finalize()
        } catch {
            // A single row's bookkeeping must not block the rest of the flush.
        }
    })
}

function registerFlushListeners() {
    if (flushListenersRegistered || typeof window === 'undefined') return
    flushListenersRegistered = true

    // Final flush when the page goes away or backgrounds; keepalive lets the
    // request outlive the document.
    window.addEventListener('pagehide', () => {
        finalizeActiveExposures()
        void flushQuestImpressions({ keepalive: true })
    })
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            // A hidden tab is not being read, so the exposure is genuinely over.
            // Coming back and continuing to read starts no new event for the
            // same card — a deliberate simplification, noted in the doc.
            finalizeActiveExposures()
            void flushQuestImpressions({ keepalive: true })
        } else if (queuedImpressions.size > 0 || queuedEvents.size > 0) {
            scheduleFlush()
        }
    })
}

export function enqueueQuestImpression(
    questId?: string | null,
    context: { userId?: string | null; spaceId?: string | null } = {},
) {
    if (!questId || !UUID_RE.test(questId)) return
    if (!questImpressionsEnabled()) return
    const userId = context.userId || activeUserId()
    const spaceId = context.spaceId || activeSpaceId()
    if (!userId || !spaceId) return

    const key = questImpressionKey(userId, spaceId, questId)
    if (recordedImpressions.has(key) || queuedImpressions.has(key)) return

    queuedImpressions.set(key, { userId, spaceId, questId, shownAtMs: Date.now() })
    registerFlushListeners()
    scheduleFlush()
}

function rememberExposure(subjectId: string, context: ExposureContext) {
    // Delete-then-set keeps Map insertion order as an LRU.
    recentExposures.delete(subjectId)
    recentExposures.set(subjectId, context)
    if (recentExposures.size > MAX_REMEMBERED_EXPOSURES) {
        const oldest = recentExposures.keys().next().value
        if (oldest !== undefined) recentExposures.delete(oldest)
    }
}

type EngagementEventInput = Omit<
    QueuedEngagementEvent,
    'clientEventId' | 'sessionId' | 'occurredAtMs'
> & { occurredAtMs?: number }

function enqueueEngagementEvent(event: EngagementEventInput) {
    if (!questImpressionsEnabled()) return
    const subjectId = event.questId || event.ordinalId
    if (!subjectId) return

    const key = engagementEventKey(
        event.userId,
        event.spaceId,
        subjectId,
        event.surface,
        event.eventType,
        event.scope,
        event.orderKey,
    )
    if (sentEventKeys.has(key)) return
    sentEventKeys.add(key)

    // The client id is what makes an at-least-once flush exactly-once in the
    // table: a retried batch collides on the unique index and is skipped.
    const clientEventId = uuid()
    queuedEvents.set(clientEventId, {
        ...event,
        clientEventId,
        sessionId: analyticsSessionId(),
        occurredAtMs: event.occurredAtMs ?? Date.now(),
    })
    registerFlushListeners()
    scheduleFlush()
}

// Click-through. Called from the panel-open funnel; `filter` should be the
// filter of the panel the user clicked FROM. A remembered exposure supplies
// attribution details only when that complete context matches.
export function recordEngagementOpen(
    questId?: string | null,
    context: {
        filter?: unknown
        ordinalId?: string | null
        spaceId?: string | null
    } = {},
) {
    if (!questImpressionsEnabled()) return

    const validQuestId = validUuid(questId)
    const validOrdinalId = validUuid(context.ordinalId)
    const subjectId = validQuestId || validOrdinalId
    if (!subjectId) return

    const exposure = recentExposures.get(subjectId)
    const spaceId = exposure?.spaceId || validUuid(context.spaceId) || activeSpaceId()
    const userId = activeUserId()
    if (!userId || !spaceId) return

    // The panel the user clicked FROM is ground truth for the open's surface —
    // a remembered Home exposure must not claim a click that actually came from
    // Search. The remembered exposure supplies only the rank, and only when its
    // surface, scope, and ordering all match. When the source panel resolves to
    // nothing (permalinks, notification arrivals), the last exposure is the
    // best guess; failing both, `unknown` keeps click-through rates honest and makes
    // instrumentation gaps visible in the data instead of invisible.
    const clicked = resolveEngagementSurface(context.filter as Record<string, any> | null)
    const surfaceContext: EngagementSurface =
        clicked.surface !== UNKNOWN_SURFACE ? clicked : exposure || clicked
    const position =
        exposure && sameEngagementSurfaceContext(exposure, surfaceContext)
            ? exposure.position
            : null

    enqueueEngagementEvent({
        userId,
        spaceId,
        questId: validQuestId,
        ordinalId: validOrdinalId,
        eventType: 'open',
        surface: surfaceContext.surface,
        scope: surfaceContext.scope,
        orderKey: surfaceContext.orderKey,
        position,
    })
}

export async function flushQuestImpressions({ keepalive = false } = {}): Promise<void> {
    if (trackingDisabled) return
    if (queuedImpressions.size === 0 && queuedEvents.size === 0) return

    const userId = activeUserId()
    if (!userId) return

    // A login change in a long-lived browser session must never submit the
    // previous user's impressions with the new user's credentials.
    for (const [key, impression] of queuedImpressions) {
        if (impression.userId !== userId) queuedImpressions.delete(key)
    }
    for (const [key, event] of queuedEvents) {
        if (event.userId !== userId) queuedEvents.delete(key)
    }

    const impressionBatch = Array.from(queuedImpressions.entries()).slice(0, MAX_BATCH)
    const eventBatch = Array.from(queuedEvents.entries()).slice(0, MAX_BATCH)
    if (impressionBatch.length === 0 && eventBatch.length === 0) return
    impressionBatch.forEach(([key]) => queuedImpressions.delete(key))
    eventBatch.forEach(([key]) => queuedEvents.delete(key))

    const requeue = () => {
        impressionBatch.forEach(([key, impression]) => queuedImpressions.set(key, impression))
        eventBatch.forEach(([key, event]) => queuedEvents.set(key, event))
    }

    try {
        const response = await fetch(`${window.knovApiUrl}/api/v1/quests/impressions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...api.commonHeaders(),
                ...accessTokens(),
                'X-CSRF-Token': gon.formToken,
            },
            credentials: 'same-origin',
            keepalive,
            body: JSON.stringify({
                impressions: serializeQuestImpressions(
                    impressionBatch.map(([, impression]) => impression),
                ),
                events: serializeEngagementEvents(eventBatch.map(([, event]) => event)),
            }),
        })

        if (response.ok) {
            impressionBatch.forEach(([key]) => recordedImpressions.add(key))
        } else if (response.status === 401 || response.status === 403) {
            trackingDisabled = true
        } else {
            requeue()
        }
    } catch {
        // Transient network failure: requeue; the next impression reschedules.
        requeue()
    }

    // A visibilitychange keepalive flush can fail after the page is visible
    // again. In that case, restart the retry timer instead of stranding rows.
    if (
        (queuedImpressions.size > 0 || queuedEvents.size > 0) &&
        !trackingDisabled &&
        (!keepalive || document.visibilityState === 'visible')
    ) {
        scheduleFlush()
    }
}

interface UseQuestImpressionArgs {
    elementRef: React.RefObject<HTMLElement | null>
    questId?: string | null
    // Ordinal tiles whose thread has not been created yet have no quest id. They
    // still produce a real exposure, and dropping them would bias the log toward
    // ordinals that already have engagement.
    ordinalId?: string | null
    // The subject's actual space. Aggregate surfaces (for example Memes) can
    // show a public thread from a different space than the active panel; using
    // the active space would make the server correctly reject that event as a
    // quest/space mismatch. The TOP NEW projection still uses the active space.
    spaceId?: string | null
    // Rank within the served list. Without it an impression cannot be corrected
    // for position bias, which makes it near-useless as a training label.
    position?: number | null
    // The stream's filter, resolved to surface/scope/order for the event log.
    filter?: unknown
    isDraft?: boolean
    rootRef?: React.RefObject<HTMLElement | null> | null
    disabled?: boolean
}

// Attach to a stream row (or ordinal tile): observes the element, marks it seen
// once it has been genuinely visible for DWELL_MS, and measures how long it
// stayed visible overall. `disabled` should carry the panel's `hide` flag —
// hidden panels use visibility:hidden, whose boxes still "intersect" — and
// checkVisibility() re-verifies at dwell time.
export function useQuestImpression({
    elementRef,
    questId,
    ordinalId = null,
    spaceId = null,
    position = null,
    filter = null,
    isDraft = false,
    rootRef = null,
    disabled = false,
}: UseQuestImpressionArgs) {
    // Memoized on the resolved VALUES, not on `filter` identity: callers pass
    // panel filters that are recreated freely, and keying the effect on object
    // identity would rebuild every visible row's observers on unrelated renders.
    const { surface, scope, orderKey } = resolveEngagementSurface(
        filter as Record<string, any> | null,
    )
    const surfaceContext = useMemo(() => ({ surface, scope, orderKey }), [surface, scope, orderKey])
    // Read at emit time rather than tracked as an effect dependency: prepending
    // "N New" items shifts every row's index at once, and that must not tear
    // down and restart the exposures currently being measured.
    const positionRef = useRef(position)
    positionRef.current = position

    useEffect(() => {
        if (disabled || isDraft) return
        if (!questImpressionsEnabled()) return

        const validQuestId = validUuid(questId)
        const validOrdinalId = validUuid(ordinalId)
        const subjectId = validQuestId || validOrdinalId
        if (!subjectId) return

        const userId = activeUserId()
        const projectionSpaceId = activeSpaceId()
        const eventSpaceId = validUuid(spaceId) || projectionSpaceId
        if (!userId || !projectionSpaceId || !eventSpaceId) return
        if (typeof IntersectionObserver === 'undefined') return

        const el = elementRef.current
        if (!el) return

        const projectionKey = validQuestId
            ? questImpressionKey(userId, projectionSpaceId, validQuestId)
            : null
        let projectionRecorded = !projectionKey || recordedImpressions.has(projectionKey)
        const eventKey = engagementEventKey(
            userId,
            eventSpaceId,
            subjectId,
            surfaceContext.surface,
            'impression',
            surfaceContext.scope,
            surfaceContext.orderKey,
        )
        let eventEmitted = sentEventKeys.has(eventKey)
        // Already fully recorded this session: no observer needed at all. This is
        // what keeps re-scrolling a seen feed free.
        if (projectionRecorded && eventEmitted) return

        let dwellTimer: number | null = null
        let isQualifying = false
        let shownMarked = false
        // The rank the row actually occupied when it became "shown" — the honest
        // value for position-bias correction, even if the list shifts later.
        let shownPosition: number | null = null
        let visibleSinceMs: number | null = null
        let dwellMs = 0
        // When the exposure STARTED — the event's occurred_at, so that a join
        // like "upvalue created BETWEEN occurred_at AND occurred_at + 1h"
        // catches engagement that happened mid-read, not only after scroll-away.
        let exposureStartMs: number | null = null
        let observer: IntersectionObserver | null = null
        let resizeObserver: ResizeObserver | null = null

        const clearDwell = () => {
            if (dwellTimer != null) {
                window.clearTimeout(dwellTimer)
                dwellTimer = null
            }
        }

        const teardownObservers = () => {
            observer?.disconnect()
            observer = null
            resizeObserver?.disconnect()
            resizeObserver = null
        }

        const openSegment = () => {
            if (visibleSinceMs == null) {
                visibleSinceMs = Date.now()
                // Remember as soon as a real qualifying exposure begins, not
                // only after one continuous second. A fast click still needs
                // the card's actual space, surface and rank.
                rememberExposure(subjectId, {
                    ...surfaceContext,
                    spaceId: eventSpaceId,
                    position: positionRef.current,
                })
            }
            if (exposureStartMs == null) exposureStartMs = visibleSinceMs
        }

        // Dwell is the SUM of visible stretches, not one continuous run: a card
        // read in two glances was still read for the total. The projection above
        // keeps its stricter continuous-second rule.
        const closeSegment = () => {
            if (visibleSinceMs == null) return
            dwellMs = Math.min(
                MAX_TRACKED_DWELL_MS,
                dwellMs + Math.max(0, Date.now() - visibleSinceMs),
            )
            visibleSinceMs = null
        }

        const markShown = () => {
            if (shownMarked) return
            shownMarked = true
            shownPosition = positionRef.current

            rememberExposure(subjectId, {
                ...surfaceContext,
                spaceId: eventSpaceId,
                position: shownPosition,
            })
            if (!projectionRecorded && validQuestId) {
                enqueueQuestImpression(validQuestId, { userId, spaceId: projectionSpaceId })
                projectionRecorded = true
            }
            // Nothing further to learn from this row this session.
            if (eventEmitted) {
                activeExposures.delete(finalizeExposure)
                teardownObservers()
            }
        }

        const finalizeExposure = () => {
            closeSegment()
            if (eventEmitted) return
            if (dwellMs < DWELL_MS) {
                // A sitting that never qualified is discarded outright. "Hiding
                // the tab ends the exposure" must not let sub-threshold
                // fragments from different sittings accumulate into one
                // mixed-semantics dwell value; a later sitting starts fresh.
                dwellMs = 0
                exposureStartMs = null
                return
            }

            eventEmitted = true
            enqueueEngagementEvent({
                userId,
                spaceId: eventSpaceId,
                questId: validQuestId,
                ordinalId: validOrdinalId,
                eventType: 'impression',
                surface: surfaceContext.surface,
                scope: surfaceContext.scope,
                orderKey: surfaceContext.orderKey,
                position: shownPosition ?? positionRef.current,
                dwellMs,
                occurredAtMs: exposureStartMs ?? Date.now(),
            })
            activeExposures.delete(finalizeExposure)
            if (projectionRecorded) teardownObservers()
        }

        const startDwell = () => {
            if (dwellTimer != null || shownMarked) return
            dwellTimer = window.setTimeout(() => {
                dwellTimer = null
                if (!isQualifying || document.visibilityState !== 'visible') return
                const checkVisibility = (el as HTMLElement & { checkVisibility?: () => boolean })
                    .checkVisibility
                if (typeof checkVisibility === 'function' && !checkVisibility.call(el)) return

                markShown()
            }, DWELL_MS)
        }

        const syncDwellState = () => {
            if (isQualifying && document.visibilityState === 'visible') {
                if (!eventEmitted) openSegment()
                startDwell()
            } else {
                closeSegment()
                clearDwell()
            }
        }

        const handleEntries: IntersectionObserverCallback = entries => {
            const entry = entries[entries.length - 1]
            if (!entry) return
            const visibleHeight = entry.intersectionRect?.height || 0
            isQualifying = qualifiesQuestImpression(
                entry.isIntersecting,
                entry.intersectionRatio,
                visibleHeight,
            )

            syncDwellState()
            // Non-virtualized grids keep old cards mounted indefinitely. Fully
            // leaving the viewport is therefore the normal end of an exposure,
            // not something that can wait for unmount/pagehide (where a single
            // keepalive batch would lose the tail).
            if (!entry.isIntersecting) finalizeExposure()
        }

        let visibilityThreshold = -1
        const observeAtHeight = (height: number) => {
            const nextThreshold = questImpressionThresholdForHeight(height)
            if (Math.abs(nextThreshold - visibilityThreshold) < 0.001) return
            // A rebuilt observer re-delivers current state, so closing the open
            // segment here keeps dwell from being double-counted.
            closeSegment()

            visibilityThreshold = nextThreshold
            isQualifying = false
            clearDwell()
            observer?.disconnect()
            observer = new IntersectionObserver(handleEntries, {
                root: rootRef?.current || null,
                threshold:
                    nextThreshold === QUEST_IMPRESSION_VISIBLE_RATIO
                        ? [0, QUEST_IMPRESSION_VISIBLE_RATIO]
                        : [0, nextThreshold, QUEST_IMPRESSION_VISIBLE_RATIO],
            })
            observer.observe(el)
        }

        if (typeof ResizeObserver === 'undefined') {
            observeAtHeight(el.getBoundingClientRect().height || el.offsetHeight)
        } else {
            // Start observing immediately at the ratio fallback. ResizeObserver
            // then supplies height without a synchronous layout read and swaps
            // in the exact 240px threshold before paint settles.
            observeAtHeight(0)
            resizeObserver = new ResizeObserver(entries => {
                const entry = entries[entries.length - 1]
                if (entry) observeAtHeight(entry.contentRect.height)
            })
            resizeObserver.observe(el)
        }

        const handleVisibilityChange = () => syncDwellState()
        document.addEventListener('visibilitychange', handleVisibilityChange)
        activeExposures.add(finalizeExposure)
        // An exposure that never enqueues anything still needs the teardown
        // listeners registered, or its dwell dies with the page.
        registerFlushListeners()

        return () => {
            activeExposures.delete(finalizeExposure)
            clearDwell()
            // Unmount is the normal end of an exposure: the row scrolled out of
            // the virtualized window, or the panel closed.
            finalizeExposure()
            teardownObservers()
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [elementRef, questId, ordinalId, spaceId, surfaceContext, isDraft, rootRef, disabled])
}
